import React, { useMemo, useState, useCallback } from "react";
import { MdClose } from "react-icons/md";
import { TRACK_KEYS, getTrackGradient, getTrackAccentColors, getTrackTypeFromKey } from "../utils/trackHelpers";
import { getCartLineSubtotalForVariant, getCartQuantityForVariant } from "../utils/cartHelpers";
import { useTrackContext } from "@/state/TrackContext";
import { getEffectivePostcodeDiscount } from "@/shared/utils/postcodeDiscount";
import { getSessionUnitPrice, isLargeGroupBooking } from "@/shared/utils/sessionPrice";
import { useUrlParams } from "@/hooks/useUrlParams";
import { TTrackTypes } from "@/types";

// Custom scrollbar styles for session pills
const scrollbarStyles = `
  .session-pills-scroll::-webkit-scrollbar {
    height: 6px;
  }
  .session-pills-scroll::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 3px;
  }
  .session-pills-scroll::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
  .session-pills-scroll::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

interface SessionPillsProps {
  trackKey: string;
  sessions: any[];
  cartData: any[];
  productRenderedLocation: string;
  defaultQuantity?: number;
  getSessionQuantity?: (variantId: string, defaultQuantity?: number) => number;
  onEditDate?: (date: string) => void;
  onRemoveSession: (sessionId: string, variantTitle: string, productTitle: string, trackKey: string, sessions: any[]) => void;
  onSessionClick?: (sessionId: string, trackKey: string) => void;
  /** Rendered on the right of the first date / section header (e.g. mobile minimise). */
  headerTrailingContent?: React.ReactNode;
}

export const SessionPills: React.FC<SessionPillsProps> = React.memo(
  ({ trackKey, sessions, cartData, productRenderedLocation, defaultQuantity = 1, getSessionQuantity, onRemoveSession, onSessionClick, headerTrailingContent }) => {
    const getSessionInstanceKey = useCallback((session: any): string => {
      const variantId = session?.variantsSelectedObject?.id || "unknown-variant";
      const selectedAt = session?.variantSelectedTime ? new Date(session.variantSelectedTime).getTime() : "no-time";
      const variantTitle = session?.variantsSelectedObject?.title || "no-variant-title";
      const productTitle = session?.variantsSelectedObject?.product?.title || "no-product-title";
      return `${variantId}-${selectedAt}-${variantTitle}-${productTitle}`;
    }, []);

    const {
      customerInfo: { isCustomerLogged, isCustomerActiveSubscriber },
      membershipInfo,
      visitorLocation,
      mainTrackSelection,
      intermediateTrackSelection,
      juniorTrackSelection,
      miniTrackSection,
      battleKartSelection,
    } = useTrackContext();
    const trackType = getTrackTypeFromKey(trackKey);
    // Seats-screen count for this pill's track. Decides large-group pricing for sessions
    // that aren't in the cart yet; cart lines carry their own flag.
    const seatsByTrack: Partial<Record<TTrackTypes, number>> = {
      MAIN_TRACK: mainTrackSelection.racesSeats.numberOfSeats,
      INTERMEDIATE_TRACK: intermediateTrackSelection.racesSeats.numberOfSeats,
      JUNIOR_TRACK: juniorTrackSelection.racesSeats.numberOfSeats,
      MINI_TRACK: miniTrackSection.racesSeats.numberOfSeats,
      BATTLE_KART_TRACK: battleKartSelection.racesSeats.numberOfSeats,
    };
    const trackSeats = (trackType && seatsByTrack[trackType]) || 0;
    const isArcade = trackKey === TRACK_KEYS.ARCADE;
    const isMembership = trackKey === TRACK_KEYS.MEMBERSHIP;
    const isBattleKart = trackKey === TRACK_KEYS.BK;
    const { getUrlParams } = useUrlParams();
    const urlParams = getUrlParams();

    // Filter out Booking Protection sessions
    const filteredSessions = useMemo(() => {
      return sessions.filter((session) => {
        const productTitle = session.variantsSelectedObject?.product?.title || "";
        const variantTitle = session.variantsSelectedObject?.title || "";
        const isBookingProtection = productTitle.toLowerCase().includes("booking protection") || variantTitle.toLowerCase().includes("booking protection");
        return !isBookingProtection;
      });
    }, [sessions]);

    // Drag scroll state - works per container using event target
    const [isDragging, setIsDragging] = useState(false);
    const [dragState, setDragState] = useState({ startX: 0, scrollLeft: 0 });

    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      const container = e.currentTarget;
      setIsDragging(true);
      setDragState({
        startX: e.pageX - container.offsetLeft,
        scrollLeft: container.scrollLeft,
      });
    }, []);

    const handleMouseMove = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isDragging) return;
        e.preventDefault();
        const container = e.currentTarget;
        const x = e.pageX - container.offsetLeft;
        const walk = (x - dragState.startX) * 1.5;
        container.scrollLeft = dragState.scrollLeft - walk;
      },
      [isDragging, dragState],
    );

    const handleMouseUp = useCallback(() => {
      setIsDragging(false);
    }, []);

    const handleMouseLeave = useCallback(() => {
      setIsDragging(false);
    }, []);

    // Helper to get quantity - uses getSessionQuantity if provided, otherwise falls back to cart quantity
    const getQuantityForDisplay = useCallback(
      (variantId: string): number => {
        if (getSessionQuantity) {
          return getSessionQuantity(variantId, defaultQuantity);
        }
        return getCartQuantityForVariant(variantId, cartData, productRenderedLocation);
      },
      [getSessionQuantity, defaultQuantity, cartData, productRenderedLocation],
    );

    // Theme colors from centralized helpers
    const pillGradient = getTrackGradient(trackKey);
    const accentColors = getTrackAccentColors(trackKey);
    const priceBackground = accentColors.primary;
    const accentColor = accentColors.secondary;
    const shouldApplyMembershipPrice = useMemo(() => {
      const membershipEligibleTracks: string[] = [
        TRACK_KEYS.MAIN,
        TRACK_KEYS.MINI,
        TRACK_KEYS.INTERMEDIATE,
        TRACK_KEYS.JUNIOR,
        TRACK_KEYS.BK,
      ];
      if (!membershipEligibleTracks.includes(trackKey)) return false;
      return membershipInfo.isMembershipPriceEnabled === true;
    }, [trackKey, membershipInfo.isMembershipPriceEnabled]);

    const getSessionPillPriceBubbleStyle = useCallback(
      (price: number) => {
        if (isBattleKart) return { backgroundColor: priceBackground };
        if (price === 0 || shouldApplyMembershipPrice) {
          return { background: "linear-gradient(90deg, #73f3fd 0%, #69abf8 100%)" };
        }
        return { backgroundColor: "#40bfff" };
      },
      [isBattleKart, priceBackground, shouldApplyMembershipPrice],
    );

    const shouldDisableRaceDiscounts = (isCustomerLogged && isCustomerActiveSubscriber) || membershipInfo.isMembershipItemAddedToCart;

    const formatPrice = useCallback((value: number, forceTwoDecimals = false): string => {
      const fixed = (Math.round(value * 100) / 100).toFixed(2);
      const hasNonZeroDecimals = /\.\d*[1-9]\d*$/.test(fixed);

      if (forceTwoDecimals && hasNonZeroDecimals) {
        return fixed;
      }

      return hasNonZeroDecimals ? fixed : fixed.replace(/\.00$/, "");
    }, []);

    const getDisplayPrice = useCallback(
      (session: any): number => {
        const variant = session.variantsSelectedObject;
        const sessionPrice = getSessionUnitPrice({
          basePrice: Number(variant?.priceV2?.amount || variant?.price || 0),
          postcodeDiscount: getEffectivePostcodeDiscount(visitorLocation.discountAmount || 0, variant?.postcodeDiscountAdjustment),
          isLargeGroup: variant?.largeGroupSurcharge || isLargeGroupBooking(trackSeats),
          track: trackType,
        });

        return shouldApplyMembershipPrice ? Math.max(0, sessionPrice - 19) : sessionPrice;
      },
      [shouldApplyMembershipPrice, visitorLocation.discountAmount, trackSeats, trackType],
    );

    const getSessionDateKey = useCallback((session: any): string => {
      const { product, title } = session.variantsSelectedObject || {};
      if (product?.title) {
        const datePart = product.title.split("|")[0]?.trim();
        if (datePart) return datePart;
      }
      if (title) {
        const datePart = title.split("|")[0]?.trim();
        if (datePart) return datePart;
      }
      return "Unknown Date";
    }, []);

    // Group sessions by date and sort by date
    const sessionsByDate = useMemo(() => {
      const grouped = filteredSessions.reduce(
        (acc, session) => {
          const dateKey = getSessionDateKey(session);

          if (!acc[dateKey]) {
            acc[dateKey] = [];
          }
          acc[dateKey].push(session);
          return acc;
        },
        {} as Record<string, any[]>,
      );

      // Sort entries by date
      const sortedEntries = Object.entries(grouped).sort(([dateA], [dateB]) => {
        const cleanA = dateA.replace(/(\d+)(st|nd|rd|th)/gi, "$1");
        const cleanB = dateB.replace(/(\d+)(st|nd|rd|th)/gi, "$1");
        const parsedA = new Date(cleanA);
        const parsedB = new Date(cleanB);

        if (!isNaN(parsedA.getTime()) && !isNaN(parsedB.getTime())) {
          return parsedA.getTime() - parsedB.getTime();
        }
        return 0;
      });

      return Object.fromEntries(sortedEntries);
    }, [filteredSessions, getSessionDateKey]);

    // Apply 20%/25% allocation once across all visible sessions for the track.
    // Then map the resulting prices back into each date group.
    const globalSessionPricing = useMemo(() => {
      const baseLineData = filteredSessions.map((session) => {
        const variantId = session.variantsSelectedObject.id;
        const sessionKey = getSessionInstanceKey(session);
        const unitPrice = getDisplayPrice(session);
        const quantity = getQuantityForDisplay(variantId);
        const safeQuantity = quantity > 0 ? quantity : 1;
        const lineSubtotal = getCartLineSubtotalForVariant(variantId, cartData, productRenderedLocation);
        // Cart-backed line prices from /cart.json only reflect LINE-level discounts. The display
        // discounts Shopify applies at the ORDER level — membership pricing and the postcode/visitor
        // discount (which floors each session to its track minimum) — reduce the cart's total_price
        // but are NOT folded into the per-line price. So when such a discount is active we trust the
        // computed display price (getDisplayPrice, which mirrors that flooring) instead of the raw
        // cart subtotal. This makes the Sub Total reconcile with the Total and keeps a session's
        // price stable when it moves from pending to in-cart.
        const hasVisitorDiscount = (visitorLocation.discountAmount || 0) > 0;
        const shouldUseComputedPricing = shouldApplyMembershipPrice || hasVisitorDiscount;
        const hasCartLineSubtotal = lineSubtotal !== null && !shouldUseComputedPricing;
        const lineTotal = shouldUseComputedPricing ? unitPrice * safeQuantity : lineSubtotal !== null ? lineSubtotal : unitPrice * safeQuantity;
        const dateKey = getSessionDateKey(session);

        return {
          sessionKey,
          variantId,
          unitPrice,
          quantity: safeQuantity,
          lineTotal,
          hasCartLineSubtotal,
          dateKey,
        };
      });

      const lineTotals = baseLineData.map((item) => item.lineTotal);
      const discountedLineTotals = [...lineTotals];

      const isMainTrack = trackKey === TRACK_KEYS.MAIN;
      const isBattleKartTrack = trackKey === TRACK_KEYS.BK;
      if (!shouldDisableRaceDiscounts && (isMainTrack || isBattleKartTrack)) {
        const hasFullQualification = isMainTrack
          ? urlParams.qualifiedFor20 === "true" && urlParams.qualifiedFor25 === "true"
          : urlParams.bkQualifiedFor20 === "true" && urlParams.bkQualifiedFor25 === "true";

        const is20OnThirdPath = isMainTrack
          ? !hasFullQualification && urlParams.qualifiedFor20OnThird === "true" && urlParams.discount20Applied === "true" && urlParams.discount25Applied !== "true"
          : !hasFullQualification && urlParams.bkQualifiedFor20OnThird === "true" && urlParams.bkDiscount20Applied === "true" && urlParams.bkDiscount25Applied !== "true";

        const has25Discount = isMainTrack ? hasFullQualification || urlParams.discount25Applied === "true" : hasFullQualification || urlParams.bkDiscount25Applied === "true";

        const has20Discount = isMainTrack ? hasFullQualification || urlParams.discount20Applied === "true" : hasFullQualification || urlParams.bkDiscount20Applied === "true";

        // 20% = 2nd race (needs >= 2 races), 25% = 3rd race (needs >= 3 races). Gate on the
        // total number of sessions on this track so a stale discount25Applied flag doesn't
        // show the 3rd-race discount when only 2 sessions are selected.
        const canApply20 = has20Discount && baseLineData.length >= 2;
        const canApply25 = has25Discount && baseLineData.length >= 3;

        if (canApply20 || canApply25) {
          // Only apply preview discounts to sessions not already priced by cart data.
          // Cart-backed items from /cart.json already include applied discounts.
          const previewIndexes = baseLineData
            .map((item, index) => ({ index, lineTotal: lineTotals[index], isPreview: !item.hasCartLineSubtotal }))
            .filter((item) => item.isPreview);

          const sortedIndexes = previewIndexes.sort((a, b) => a.lineTotal - b.lineTotal).map((item) => item.index);

          const cheapestIndex = sortedIndexes[0];
          const secondCheapestIndex = sortedIndexes[1];

          if (is20OnThirdPath && canApply20) {
            if (cheapestIndex !== undefined) {
              discountedLineTotals[cheapestIndex] = discountedLineTotals[cheapestIndex] * 0.8;
            }
          } else if (canApply25 && canApply20) {
            if (cheapestIndex !== undefined) {
              discountedLineTotals[cheapestIndex] = discountedLineTotals[cheapestIndex] * 0.75;
            }
            if (secondCheapestIndex !== undefined) {
              discountedLineTotals[secondCheapestIndex] = discountedLineTotals[secondCheapestIndex] * 0.8;
            }
          } else if (canApply20) {
            if (cheapestIndex !== undefined) {
              discountedLineTotals[cheapestIndex] = discountedLineTotals[cheapestIndex] * 0.8;
            }
          } else if (canApply25) {
            if (cheapestIndex !== undefined) {
              discountedLineTotals[cheapestIndex] = discountedLineTotals[cheapestIndex] * 0.75;
            }
          }
        }
      }

      const sessionUnitPriceByKey = new Map<string, number>();
      const subtotalByDate = new Map<string, number>();
      const discountedSessionKeys = new Set<string>();

      baseLineData.forEach((item, index) => {
        const discountedLineTotal = discountedLineTotals[index];
        const discountedUnitPrice = discountedLineTotal / item.quantity;
        sessionUnitPriceByKey.set(item.sessionKey, discountedUnitPrice);
        subtotalByDate.set(item.dateKey, (subtotalByDate.get(item.dateKey) || 0) + discountedLineTotal);
        if (Math.abs(discountedLineTotal - item.lineTotal) > 0.0001) {
          discountedSessionKeys.add(item.sessionKey);
        }
      });

      return {
        sessionUnitPriceByKey,
        subtotalByDate,
        discountedSessionKeys,
      };
    }, [
      shouldDisableRaceDiscounts,
      filteredSessions,
      getDisplayPrice,
      getQuantityForDisplay,
      getSessionDateKey,
      getSessionInstanceKey,
      cartData,
      productRenderedLocation,
      trackKey,
      shouldApplyMembershipPrice,
      visitorLocation.discountAmount,
      urlParams.qualifiedFor20,
      urlParams.qualifiedFor25,
      urlParams.bkQualifiedFor20,
      urlParams.bkQualifiedFor25,
      urlParams.qualifiedFor20OnThird,
      urlParams.bkQualifiedFor20OnThird,
      urlParams.discount20Applied,
      urlParams.discount25Applied,
      urlParams.bkDiscount20Applied,
      urlParams.bkDiscount25Applied,
    ]);

    // For membership - simple display with gradient container
    if (isMembership) {
      return (
        <div className="tw-w-full" data-hlc-field="bottom_summary.session_pills">
          {/* Membership header */}
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-1 tw-px-1">
            <h3 className="booking-label tw-text-white tw-text-[13px] tw-font-semibold">Membership</h3>
            {headerTrailingContent}
          </div>
          <div className="tw-rounded-[8px] tw-p-[2px] tw-w-full" style={{ background: pillGradient }}>
            <div className="tw-bg-[#393B41] tw-rounded-[6px] tw-w-full">
              {filteredSessions.map((session, index) => {
                const { id, title, product } = session.variantsSelectedObject;
                const quantity = getQuantityForDisplay(id);
                const displayTitle = product?.title || title || `Item ${index + 1}`;

                return (
                  <div key={`${getSessionInstanceKey(session)}-${index}`} className="tw-flex tw-items-center tw-justify-between tw-py-2.5 tw-px-3 tw-bg-[#555758] tw-rounded-lg tw-mb-2 last:tw-mb-0">
                    <div className="tw-flex tw-flex-col tw-flex-1">
                      <span className="tw-text-white tw-text-[13px] tw-font-medium">{displayTitle}</span>
                      <span className="tw-text-gray-300 tw-text-[12px]">Quantity: {quantity}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveSession(id, displayTitle, displayTitle, trackKey, [session]);
                      }}
                      className="tw-w-6 tw-h-6 tw-bg-gray-600 hover:tw-bg-gray-500 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-colors tw-duration-200"
                      title="Remove"
                    >
                      <MdClose className="tw-w-4 tw-h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    // For arcade - styled like other tracks with pill display and gradient container
    if (isArcade) {
      // Calculate subtotal for arcade items
      const arcadeSubTotal = filteredSessions.reduce((total, session) => {
        const lineSubtotal = getCartLineSubtotalForVariant(session.variantsSelectedObject.id, cartData, productRenderedLocation);
        if (lineSubtotal !== null) {
          return total + lineSubtotal;
        }

        const price = getDisplayPrice(session);
        const quantity = getQuantityForDisplay(session.variantsSelectedObject.id);
        return total + price * quantity;
      }, 0);

      return (
        <div className="tw-w-full" data-hlc-field="bottom_summary.session_pills">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-0.5 tw-px-1">
            <h3 className="booking-label tw-text-white tw-text-[12px] tw-font-semibold">Arcade Card</h3>
            {headerTrailingContent}
          </div>
          <div className="tw-rounded-[8px] tw-p-[2px] tw-w-full" style={{ background: pillGradient }}>
            <div className="tw-bg-[#393B41] tw-rounded-[6px] tw-w-full">
              {/* Horizontal scrollable arcade pills */}
              <style>{scrollbarStyles}</style>
              <div className="tw-flex tw-items-stretch">
                {/* Scrollable arcade pills */}
                <div
                  className={`session-pills-scroll tw-flex tw-gap-[10px] tw-overflow-x-auto tw-flex-1 tw-px-[6px] tw-py-[6px] tw-pb-[8px] tw-items-center ${isDragging ? "tw-cursor-grabbing" : "tw-cursor-grab"}`}
                  style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255, 255, 255, 0.3) rgba(0, 0, 0, 0.3)" }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                >
                  {filteredSessions.map((session, index) => {
                    const { id, title, product } = session.variantsSelectedObject;
                    const price = getDisplayPrice(session);
                    const quantity = getQuantityForDisplay(id);
                    const displayTitle = product?.title || title || "Arcade Card";
                    // Format as "Arcade Card, X tap" where X is the variant title (10, 20, 50)
                    const tapCount = title && /^\d+$/.test(title) ? title : null;
                    const cardValue = tapCount ? `Arcade Card, ${tapCount} tap` : displayTitle;

                    return (
                      <div
                        key={`${getSessionInstanceKey(session)}-${index}`}
                        className={`tw-relative tw-flex-shrink-0 tw-rounded-lg tw-w-fit ${onSessionClick ? "tw-cursor-pointer" : ""}`}
                        onClick={() => onSessionClick && onSessionClick(id, trackKey)}
                      >
                        <div className="tw-bg-[#555758] tw-rounded-[6px] tw-flex tw-items-center tw-overflow-hidden">
                          {/* Quantity badge */}
                          {quantity > 0 && (
                            <span
                              className="tw-absolute -tw-top-[6px] -tw-right-[2px] tw-bg-black tw-text-white tw-text-[10px] tw-font-bold tw-rounded-full tw-w-[18px] tw-h-[18px] tw-flex tw-items-center tw-justify-center tw-border"
                              style={{ borderColor: accentColor }}
                            >
                              {quantity}
                            </span>
                          )}
                          {/* Price with theme background */}
                          <span
                            className="tw-text-white tw-text-[13px] tw-font-semibold tw-px-[8px] tw-py-[4px] tw-min-w-[40px] tw-text-center"
                            style={getSessionPillPriceBubbleStyle(price)}
                          >
                            ${formatPrice(price)}
                          </span>
                          {/* Card value with black background */}
                          <span className="tw-bg-black tw-text-white tw-text-[13px] tw-font-medium tw-px-[8px] tw-py-[4px] tw-flex-1 tw-text-center tw-whitespace-nowrap">
                            {cardValue}
                          </span>
                        </div>
                        {/* Remove button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveSession(id, displayTitle, displayTitle, trackKey, [session]);
                          }}
                          className="tw-absolute -tw-top-[5px] -tw-left-[5px] tw-w-[16px] tw-h-[16px] tw-bg-gray-700 hover:tw-bg-gray-600 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-colors tw-duration-200 tw-border tw-border-gray-500"
                          title="Remove"
                        >
                          <MdClose className="tw-w-3 tw-h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Sub Total */}
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-flex-shrink-0 tw-px-[6px] tw-rounded-r-[6px]" style={{ background: accentColor }}>
                  <span className="tw-text-white tw-font-semibold tw-text-[10px]">Subtotal</span>
                  <span className="tw-text-[14px] tw-font-bold tw-text-white">${formatPrice(arcadeSubTotal)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const dateEntries = Object.entries(sessionsByDate) as [string, any[]][];

    return (
      <div className="tw-w-full tw-space-y-1" data-hlc-field="bottom_summary.session_pills">
        {dateEntries.map(([date, dateSessions], dateIndex) => {
          const dateSubtotal = globalSessionPricing.subtotalByDate.get(date) || 0;

          return (
            <div key={date} className="tw-w-full">
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-0.5 tw-px-1">
                <h3 className="booking-label tw-text-white tw-text-[12px] tw-font-semibold">{date}</h3>
                {dateIndex === 0 ? headerTrailingContent : null}
              </div>
              {/* Gradient container for each date group */}
              <div className="tw-rounded-[8px] tw-p-[2px] tw-w-full" style={{ background: pillGradient }}>
                <div className="tw-bg-[#393B41] tw-rounded-[6px] tw-w-full">
                  {/* Horizontal scrollable session pills */}
                  <style>{scrollbarStyles}</style>
                  <div className="tw-flex tw-items-stretch">
                    {/* Scrollable session pills */}
                    <div
                      className={`session-pills-scroll tw-flex tw-gap-[10px] tw-overflow-x-auto tw-flex-1 tw-px-[6px] tw-py-[4px] tw-items-center ${isDragging ? "tw-cursor-grabbing" : "tw-cursor-grab"}`}
                      style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255, 255, 255, 0.3) rgba(0, 0, 0, 0.3)" }}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseLeave}
                    >
                      {dateSessions.map((session, sessionIndex) => {
                        const { id, selectedOptions, title, product } = session.variantsSelectedObject;
                        const sessionKey = getSessionInstanceKey(session);
                        // Try to get time from selectedOptions first
                        let time = "N/A";
                        if (selectedOptions && Array.isArray(selectedOptions) && selectedOptions.length > 0) {
                          const timeOption = selectedOptions.find((opt: any) => opt.name?.toLowerCase().includes("time"));
                          if (timeOption?.value) {
                            time = timeOption.value;
                          }
                        }
                        // Fallback: Extract time from variant title (format: "Date | Time" or just time patterns)
                        if (time === "N/A" && title) {
                          const titleParts = title.split("|");
                          if (titleParts.length > 1) {
                            // Take the second part which is usually the time
                            time = titleParts[1]?.trim() || "N/A";
                          } else {
                            // Try to match time pattern (e.g., "10:00 AM", "2:30 PM")
                            const timeMatch = title.match(/\d{1,2}:\d{2}\s*(AM|PM|am|pm)?/i);
                            if (timeMatch) {
                              time = timeMatch[0];
                            }
                          }
                        }
                        const price = globalSessionPricing.sessionUnitPriceByKey.get(sessionKey) ?? getDisplayPrice(session);
                        const hasRaceDiscountApplied = globalSessionPricing.discountedSessionKeys.has(sessionKey);
                        const quantity = getQuantityForDisplay(id);
                        const productTitle = product?.title || title || "Session";

                        const roundedSessionPrice = Math.round(price * 100) / 100;
                        const sessionPriceHasDecimals = !Number.isInteger(roundedSessionPrice);

                        return (
                          <div
                            key={`${sessionKey}-${sessionIndex}`}
                            className={`tw-relative tw-flex-shrink-0 tw-rounded-lg tw-w-full ${onSessionClick ? "tw-cursor-pointer" : ""}`}
                            style={{
                              minWidth: "110px",
                              maxWidth: sessionPriceHasDecimals ? "140px" : "120px",
                            }}
                            onClick={() => onSessionClick && onSessionClick(id, trackKey)}
                          >
                            <div className="tw-bg-[#555758] tw-rounded-[6px] tw-flex tw-items-center tw-overflow-hidden tw-w-full">
                              {/* Quantity badge */}
                              {quantity > 0 && (
                                <span
                                  className="tw-absolute -tw-top-[6px] -tw-right-[2px] tw-bg-black tw-text-white tw-text-[10px] tw-font-bold tw-rounded-full tw-w-[18px] tw-h-[18px] tw-flex tw-items-center tw-justify-center tw-border"
                                  style={{ borderColor: accentColor }}
                                >
                                  {quantity}
                                </span>
                              )}
                              {/* Price with theme background */}
                              <span
                                className="tw-text-white tw-text-[13px] tw-font-semibold tw-px-[8px] tw-py-[4px] tw-min-w-[40px] tw-text-center"
                                style={getSessionPillPriceBubbleStyle(price)}
                              >
                                ${formatPrice(price, hasRaceDiscountApplied)}
                              </span>
                              {/* Time with black background */}
                              <span className="tw-bg-black tw-text-white tw-text-[13px] tw-font-medium tw-px-[8px] tw-py-[4px] tw-flex-1 tw-text-center">{time}</span>
                            </div>
                            {/* Remove button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemoveSession(id, title || time, productTitle, trackKey, [session]);
                              }}
                              className="tw-absolute -tw-top-[5px] -tw-left-[5px] tw-w-[16px] tw-h-[16px] tw-bg-gray-700 hover:tw-bg-gray-600 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-colors tw-duration-200 tw-border tw-border-gray-500"
                              title="Remove session"
                            >
                              <MdClose className="tw-w-3 tw-h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {/* Sub Total - shows per-date subtotal */}
                    <div
                      className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-flex-shrink-0 tw-px-[6px] tw-rounded-r-[6px]"
                      style={{ background: isBattleKart ? pillGradient : accentColor }}
                    >
                      <span className="tw-text-white tw-font-semibold tw-text-[10px]">Subtotal</span>
                      <span className="tw-text-[14px] tw-font-bold tw-text-white">${formatPrice(dateSubtotal)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  },
);

SessionPills.displayName = "SessionPills";
